<template>
  <div class="layout-root">
    <!-- 移动端展示模式：不渲染后台框架（顶栏/菜单/标签），仅全屏渲染当前模块 -->
    <div v-if="standaloneMode" class="standalone-page">
      <router-view></router-view>
    </div>

    <el-container v-else class="app-wrapper">
      <el-header class="app-header" height="60px">
      <div class="header-left">
        <!-- logo：内联 SVG，不依赖外链（原 element.eleme.io 域名已下线会导致图片裂开） -->
        <svg class="logo" viewBox="0 0 32 32" role="img" aria-label="代码技能库">
          <defs>
            <linearGradient id="logoGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="#66b1ff" />
              <stop offset="100%" stop-color="#409EFF" />
            </linearGradient>
          </defs>
          <rect width="32" height="32" rx="9" fill="url(#logoGrad)" />
          <!-- 代码尖括号：左 <  右 > -->
          <path
            d="M13 10.5 L8 16 L13 21.5"
            fill="none"
            stroke="#ffffff"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M19 10.5 L24 16 L19 21.5"
            fill="none"
            stroke="#ffffff"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <!-- 水墨打字：首屏标题仅 5 字，符合短文本使用约束 -->
        <ink-text class="title" text="代码技能库" :stagger="40" :duration="480" />
        <i
          :class="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'"
          style="margin-left: 20px; font-size: 20px; cursor: pointer;"
          @click="toggleSidebar"
        ></i>
      </div>
      <div class="header-right">
        <i class="el-icon-bell" style="margin-right: 20px; cursor: pointer;"></i>
        <el-dropdown trigger="click">
          <span
            class="el-dropdown-link"
            style="cursor: pointer; display: flex; align-items: center;"
          >
            <el-avatar size="small" :src="userAvatar"></el-avatar>
            <span style="margin-left: 10px;">{{ userName }}</span>
            <i class="el-icon-caret-bottom el-icon--right"></i>
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item>个人中心</el-dropdown-item>
            <el-dropdown-item divided @click.native="logout">退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </el-header>

    <el-container class="main-container">
      <el-aside :width="isCollapse ? '64px' : '220px'" class="app-sidebar">
        <el-menu
          :default-active="$route.path"
          :collapse="isCollapse"
          router
          class="el-menu-vertical-demo"
          :style="{ 'background-color': 'transparent' }"
          text-color="inherit"
          active-text-color="#409EFF"
        >
          <sidebar-menu-item
            v-for="item in menuList"
            :key="item.id || item.path || item.index"
            :item="item"
          />
        </el-menu>
      </el-aside>

      <el-main class="app-main">
        <div class="tags-view-container">
          <router-link
            v-for="tag in visitedViews"
            :key="tag.path"
            :to="{ path: tag.path, query: tag.query, fullPath: tag.fullPath }"
            tag="span"
            class="tags-view-item"
            :class="isActive(tag) ? 'active' : ''"
            @click.native="handleClick(tag)"
          >
            {{ tag.title }}
            <span
              v-if="!tag.meta.affix"
              class="el-icon-close"
              @click.prevent.stop="closeSelectedTag(tag)"
            ></span>
          </router-link>
        </div>

        <div class="content-wrapper">
          <keep-alive>
            <router-view></router-view>
          </keep-alive>
        </div>
      </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script>
import SidebarMenuItem from '@/components/SidebarMenuItem.vue'
import InkText from '@/components/InkText.vue'
import { fetchMenuTree } from '@/api/modules/menu'
import { getMenuComponentPath } from '@/utils/menuComponent'
import { isMobileDevice } from '@/utils/device'

export default {
  name: 'Layout',
  components: {
    SidebarMenuItem,
    InkText
  },
  data() {
    return {
      isCollapse: false,
      userName: 'Admin',
      userAvatar: '',
      visitedViews: [],
      menuList: [],
      menuLoading: false,
      freeViewMode: false
    }
  },
  computed: {
    /**
     * 移动端展示模式：移动设备访问 /app/wzwxq、/app/inbox 时，不渲染后台框架
     * （顶栏/左侧菜单/顶部标签栏），内容全屏独立展示。
     */
    standaloneMode() {
      const standalonePaths = ['/app/wzwxq', '/app/inbox', '/app/third-party-model']
      return isMobileDevice() && standalonePaths.includes(this.$route.path)
    },

    /**
     * 当前路由是否携带 freeView 参数。
     * freeView = 免登录只读分享模式：未登录也能进入当前模块，
     * 且左侧菜单只保留当前模块对应菜单，其余菜单项一律隐藏。
     */
    isFreeView() {
      return this.$route.query &&
        Object.prototype.hasOwnProperty.call(this.$route.query, 'freeView')
    }
  },
  watch: {
    $route() {
      this.addTags()

      // freeView 状态变化时（进入/退出分享模式）重新拉取并过滤菜单。
      if (this.isFreeView !== this.freeViewMode) {
        this.freeViewMode = this.isFreeView
        this.fetchMenuList()
      }
    },
    // 移动端展示模式结束（设备/路由变化回桌面框架）时补拉菜单
    standaloneMode(value) {
      if (!value && !this.menuList.length) {
        this.fetchMenuList()
      }
    }
  },
  created() {
    let user = {}

    try {
      const userStr = localStorage.getItem('user')
      if (userStr && userStr !== 'res.data.user') {
        user = JSON.parse(userStr)
      }
    } catch (error) {
      console.warn('Failed to parse local user data:', error)
      localStorage.removeItem('user')
    }

    this.userName = user.name || 'Admin'
    this.userAvatar = user.avatar || ''
    this.freeViewMode = this.isFreeView
    // 移动端展示模式不渲染菜单，无需请求菜单树
    if (!this.standaloneMode) {
      this.fetchMenuList()
    }
    this.initTags()
    this.addTags()
    window.addEventListener('menu-updated', this.handleMenuUpdated)
  },
  beforeDestroy() {
    window.removeEventListener('menu-updated', this.handleMenuUpdated)
  },
  methods: {
    async fetchMenuList() {
      this.menuLoading = true

      try {
        const data = await fetchMenuTree()
        const menuTree = Array.isArray(data) ? data : []
        this.menuList = this.freeViewMode
          ? this.filterFreeViewMenuTree(menuTree)
          : menuTree
      } catch (error) {
        console.error('Failed to fetch menu list:', error)
      } finally {
        this.menuLoading = false
      }
    },
    /**
     * 免登录（freeView）模式下过滤菜单树：
     * 只保留「当前模块」及其所在的目录祖先链，其余菜单项全部隐藏。
     * @param {Array} menuTree 完整菜单树
     * @returns {Array} 过滤后的菜单树
     */
    filterFreeViewMenuTree(menuTree) {
      const currentPath = this.$route.path
      const currentTitle = this.$route.meta && this.$route.meta.title

      // 递归：命中当前模块的节点（目录只透传，叶子按 path 或 title 匹配）
      const pick = (menus) => {
        const result = []

        menus.forEach(menu => {
          const children = Array.isArray(menu.children) ? menu.children : []
          const matched =
            menu.path === currentPath ||
            (menu.title && currentTitle && menu.title === currentTitle)

          // 命中当前模块：叶子节点（无子级或自身带组件路径）直接保留。
          if (matched && (!children.length || getMenuComponentPath(menu))) {
            result.push(menu)
            return
          }

          if (children.length) {
            const matchedChildren = pick(children)
            if (matchedChildren.length) {
              result.push({
                ...menu,
                children: matchedChildren
              })
            }
          }
        })

        return result
      }

      return pick(menuTree)
    },
    handleMenuUpdated() {
      this.fetchMenuList()
    },
    toggleSidebar() {
      this.isCollapse = !this.isCollapse
    },
    logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      this.$router.push('/login')
    },
    isActive(route) {
      return route.path === this.$route.path
    },
    initTags() {
      this.visitedViews = []
    },
    addTags() {
      const { name } = this.$route
      if (!name) {
        return
      }

      if (this.visitedViews.some(view => view.path === this.$route.path)) {
        return
      }

      this.visitedViews.push({
        ...this.$route,
        title: this.$route.meta.title || 'no-name'
      })
    },
    closeSelectedTag(view) {
      const index = this.visitedViews.indexOf(view)
      this.visitedViews.splice(index, 1)

      if (this.isActive(view)) {
        this.toLastView(this.visitedViews)
      }
    },
    toLastView(visitedViews) {
      const latestView = visitedViews.slice(-1)[0]
      if (latestView) {
        this.$router.push(latestView.path)
      } else {
        this.$router.push('/home')
      }
    },
    handleClick() {}
  }
}
</script>

<style>
.layout-root {
  height: 100%;
}

/* 移动端展示模式：整屏滚动容器，无后台框架 */
.standalone-page {
  height: 100%;
  min-height: 100dvh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background-color: var(--surface-2);
}

/* 独立模式下页面模块根节点撑满容器（无 12px 内边距） */
.standalone-page > * {
  min-height: 100%;
  box-sizing: border-box;
}

.app-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}
/* 顶栏：微明度分层（底色 + 极淡分层阴影），不用硬边框压边 */
.app-header {
  background-color: #3f51b5;
  color: var(--color-text-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  box-shadow: var(--shadow-surface-1);
  position: relative;
  z-index: 2;
}
.header-left {
  display: flex;
  align-items: center;
}
.header-left .logo {
  width: 30px;
  height: 30px;
  margin-right: 10px;
  /* SVG 需要 flex-shrink:0，否则在窄屏/折叠态会被压扁成椭圆 */
  flex-shrink: 0;
  display: block;
}
.header-left .title {
  font-size: 18px;
  /* 字重下沉一档，配合深色文字更耐看 */
  font-weight: 600;
  color: var(--color-text-primary);
  letter-spacing: 0.2px;
}
/* 顶栏图标：光学对齐（1/8 字高下沉） + 统一交互反馈 */
.header-left .el-icon-s-unfold,
.header-left .el-icon-s-fold,
.header-right .el-icon-bell {
  vertical-align: -0.125em;
  color: var(--color-text-secondary);
  transition: color var(--transition-duration-fast) var(--ease-out-soft),
    transform var(--transition-duration-fast) var(--ease-feedback);
}
.header-left .el-icon-s-unfold:hover,
.header-left .el-icon-s-fold:hover,
.header-right .el-icon-bell:hover {
  color: var(--color-primary);
  transform: scale(1.06);
}
/* 顶栏用户名：跟随主题色，不再是写死的白字 */
.header-right .el-dropdown-link {
  color: var(--color-text-regular);
}
.header-right .el-dropdown-link:hover {
  color: var(--color-primary);
}
.main-container {
  flex: 1;
  display: flex;
  overflow: hidden;
}
.app-sidebar {
  background-color: #eef1f6;
  border-right: 1px solid #dcdfe6;
  height: calc(100vh - 60px);
  transition: width var(--transition-duration-base) var(--ease-out-soft);
  overflow-x: hidden;
  overflow-y: auto;
}
.app-sidebar::-webkit-scrollbar {
  width: 8px;
}
.app-sidebar::-webkit-scrollbar-thumb {
  background-color: rgba(144, 147, 153, 0.45);
  border-radius: 8px;
}
.app-sidebar::-webkit-scrollbar-track {
  background-color: transparent;
}
/* 侧边菜单容器：透明底让 .app-sidebar 的 --surface-3 透出来，
   菜单项自身用微明度变化表达 hover / active，不做硬边框。 */
.el-menu-vertical-demo {
  border-right: none;
  min-height: 100%;
  background-color: transparent !important;
}
.el-menu-vertical-demo:not(.el-menu--collapse) {
  width: 220px;
}
/* 菜单项：圆角 + 左右内缩，hover 时轻微提亮并右移 2px（拖动/悬停的轻反馈） */
.el-menu-vertical-demo .el-menu-item,
.el-menu-vertical-demo .el-submenu__title {
  height: 44px;
  line-height: 44px;
  margin: 2px 8px;
  border-radius: var(--border-radius-md);
  color: var(--color-text-regular);
  background-color: transparent;
  transition: background-color var(--transition-duration-fast) var(--ease-out-soft),
    color var(--transition-duration-fast) var(--ease-out-soft),
    padding-left var(--transition-duration-fast) var(--ease-out-soft);
}
.el-menu-vertical-demo .el-menu-item:hover,
.el-menu-vertical-demo .el-submenu__title:hover {
  background-color: rgba(255, 255, 255, 0.72);
  color: var(--color-text-primary);
}
/* 选中项：提亮到 surface-1 + 左侧 3px 主色指示条（颜色通道，不只靠动画） */
.el-menu-vertical-demo .el-menu-item.is-active {
  background-color: var(--surface-1);
  color: var(--color-primary);
  font-weight: 500;
  box-shadow: var(--shadow-surface-1);
}
.el-menu-vertical-demo .el-menu-item.is-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 18px;
  border-radius: 0 2px 2px 0;
  background-color: var(--color-primary);
}
/* 菜单图标：光学对齐 1/8 字高 + 悬停轻微放大 */
.el-menu-vertical-demo .el-menu-item i,
.el-menu-vertical-demo .el-submenu__title i {
  vertical-align: -0.125em;
  margin-right: 8px;
  transition: transform var(--transition-duration-fast) var(--ease-feedback);
}
.el-menu-vertical-demo .el-menu-item:hover i,
.el-menu-vertical-demo .el-submenu__title:hover i {
  transform: scale(1.08);
}
/* 深色模式：侧边栏回到深色微明度分层 */
@media (prefers-color-scheme: dark) {
  .app-header,
  .tags-view-container {
    background-color: var(--surface-1-dark);
    border-bottom-color: rgba(255, 255, 255, 0.06);
    box-shadow: var(--shadow-surface-1-dark);
  }

  .app-sidebar,
  .app-main {
    background-color: var(--bg-color-page-dark);
  }

  .header-left .title,
  .header-right .el-dropdown-link {
    color: var(--color-text-primary-dark);
  }

  .el-menu-vertical-demo .el-menu-item,
  .el-menu-vertical-demo .el-submenu__title {
    color: var(--color-text-regular-dark);
  }

  .el-menu-vertical-demo .el-menu-item:hover,
  .el-menu-vertical-demo .el-submenu__title:hover {
    background-color: var(--surface-2-dark);
    color: var(--color-text-primary-dark);
  }

  .el-menu-vertical-demo .el-menu-item.is-active {
    background-color: var(--surface-2-dark);
  }

  .tags-view-item {
    background-color: var(--surface-2-dark);
    color: var(--color-text-regular-dark);
  }

  .tags-view-item:not(.active):hover {
    background-color: var(--surface-1-dark);
  }
}
.app-main {
  background-color: var(--surface-3);
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* 标签栏：底部分层阴影替代硬线条，与内容区形成微明度台阶 */
.tags-view-container {
  height: 34px;
  width: 100%;
  background: var(--surface-1);
  border-bottom: 1px solid #d8dce5;
  box-shadow: var(--shadow-surface-1);
  display: flex;
  align-items: center;
  padding-left: 10px;
  position: relative;
  z-index: 1;
}

/* 标签：去掉方硬描边，改用底色分层 + 圆角，hover/active 用微位移反馈 */
.tags-view-item {
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  height: 24px;
  line-height: 24px;
  border: 1px solid transparent;
  border-radius: var(--border-radius-sm);
  color: var(--color-text-regular);
  background: var(--surface-3);
  padding: 0 8px;
  font-size: 12px;
  margin-left: 5px;
  margin-top: 4px;
  transition: background-color var(--transition-duration-fast) var(--ease-out-soft),
    color var(--transition-duration-fast) var(--ease-out-soft),
    box-shadow var(--transition-duration-fast) var(--ease-out-soft);
}
.tags-view-item.active {
  background-color: var(--color-primary);
  color: #fff;
  box-shadow: var(--shadow-surface-1);
}
.tags-view-item.active::before {
  content: '';
  background: var(--surface-1);
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  position: relative;
  margin-right: 4px;
  vertical-align: -0.125em;
}
.tags-view-container .el-icon-close {
  width: 16px;
  height: 16px;
  vertical-align: -0.125em;
  border-radius: 50%;
  text-align: center;
  transition: all var(--transition-duration-fast) var(--ease-out-soft);
  transform-origin: 100% 50%;
}
.tags-view-container .el-icon-close:before {
  transform: scale(.6);
  display: inline-block;
  vertical-align: -0.125em;
}
.tags-view-container .el-icon-close:hover {
  background-color: rgba(255, 255, 255, 0.24);
}
.tags-view-item:not(.active):hover {
  background-color: var(--surface-1);
  color: var(--color-primary);
  box-shadow: var(--shadow-surface-1);
}
.content-wrapper {
  flex: 1;
  min-height: 0;
  padding: 12px;
  box-sizing: border-box;
  /* 内容区改为「模块内部滚动」：外层固定高度，页面模块自身撑满并滚动 */
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 高度契约：router-view 渲染出的页面模块根节点自动撑满内容区（去掉 12px 内边距后的高度） */
.content-wrapper > * {
  flex: 1 1 auto;
  min-height: 0;
  box-sizing: border-box;
}
</style>
